import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, first, tap, withLatestFrom} from 'rxjs/operators';
import { of } from 'rxjs';
import * as ReportsActions from '../actions/reports.actions';
import { ReportsService } from '@app/services/reports/reports.service';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { selectReportsDirectionState, selectReportsSortState } from '../selectors/reports.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { selectCoreState } from '@app/core/store/core.selectors';


@Injectable()
export class ReportsEffects {

  loadReports$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadReports),
      withLatestFrom(this.store$.select(selectCoreState)),
      switchMap(([action, coreState]) => {
        const locID = coreState?.me?.locID;
        return this._reports.getReports('prdNameEn', 'asc', action.query, null, locID).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/reports'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ReportsActions.loadReportsSuccess({ reports: data.reports, total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.loadReportsFailure({ error }))
          })
        )
      })
    );
  });

  loadPicked$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadPicked),
      switchMap(action => {
        return this._reports.getReportsPicked('created_at', 'desc', action.query).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/reports'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ReportsActions.loadPickedSuccess({ picked: data.picked, picked_total: data.picked_total })
          }),
          catchError(error => {
            return of(ReportsActions.loadPickedFailure({ error }))
          })
        )
      })
    );
  });

  loadPickActivity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadPickActivity),
      switchMap(action => {
        return this._reports.getReportsPickActivity(action.sortType, action.direction, action.query).pipe(
          map((data): any => {
            return ReportsActions.loadPickActivitySuccess({
              pickActivity: data.pickActivity,
              pickActivity_total: data.pickActivity_total
            })
          }),
          catchError(error => {
            return of(ReportsActions.loadPickActivityFailure({ error }))
          })
        )
      })
    );
  });

  loadProductSerialNumber$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadProductSerialNumber),
      switchMap(action => {
        return this._reports.getReportsProductSerialNumber('created_at', 'desc', action.query ,'', action.prdID,action.totalOnly).pipe(
          map((data): any => {
            // if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/reports'){
            //   this.store$.dispatch(CoreActions.showPermissionMessage({
            //     title: this._translation.instant('errormessage.warning'),
            //     body: this._translation.instant('errormessage.access')
            //   }));
            // }
  
            return ReportsActions.loadProductSerialNumberSuccess({ productSerialNumber: data.serial_numbers.data, product_serialnumber_total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.loadProductSerialNumberFailure({ error }))
          })
        )
      })
    );
  });

  loadReceived$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadReceived),
      switchMap(action => {
        return this._reports.getReportsReceived('created_at', 'desc', action.query).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/reports'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ReportsActions.loadReceivedSuccess({ received: data.received, received_total: data.received_total })
          }),
          catchError(error => {
            return of(ReportsActions.loadReceivedFailure({ error }))
          })
        )
      })
    );
  });

  loadReorder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadReorder),
      withLatestFrom(this.store$.select(selectCoreState)),
      switchMap(([action, coreState]) => {
        const locID = coreState?.me?.locID;
        return this._reports.getReportsReorder('prdNameEn', 'asc', action.query, null, locID).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/reports'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ReportsActions.loadReorderSuccess({ reorder: data.reorder, reorder_total: data.reorder_total })
          }),
          catchError(error => {
            return of(ReportsActions.loadReorderFailure({ error }))
          })
        )
      })
    );
  });

  loadReportsByCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.filterByCategory),
      switchMap(action => {
        return this._reports.getReportsByCategory(action.prdCatID, action.active, action.direction).pipe(
          map((data): any => {
            return ReportsActions.filterByCategorySuccess({ reports: data.reports, total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.filterByCategoryFailure({ error }))
          })
        )
      })
    );
  });

  loadReportsByReorderCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.filterByReorderCategory),
      switchMap(action => {
        return this._reports.getReportsByReorderCategory(action.prdCatID, action.active, action.direction).pipe(
          map((data): any => {
            return ReportsActions.filterByReorderCategorySuccess({ reorder: data.reorder, reorder_total: data.reorder_total })
          }),
          catchError(error => {
            return of(ReportsActions.filterByReorderCategoryFailure({ error }))
          })
        )
      })
    );
  });

  loadReportsByStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.filterByStorages),
      switchMap(action => {
        return this._reports.getReportsByStorages(action.prdStoragesIDs, action.active, action.direction).pipe(
          map((data): any => {
            return ReportsActions.filterByStoragesSuccess({ reports: data.reports, total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.filterByStoragesFailure({ error }))
          })
        )
      })
    );
  });

  loadReorderByStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.filterReorderByStorages),
      switchMap(action => {
        return this._reports.getReorderByStorages(action.prdStoragesIDs, action.active, action.direction).pipe(
          map((data): any => {
            return ReportsActions.filterReorderByStoragesSuccess({ reorder: data.reorder, reorder_total: data.reorder_total })
          }),
          catchError(error => {
            return of(ReportsActions.filterReorderByStoragesFailure({ error }))
          })
        )
      })
    );
  });

  searchReports$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReportsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReportsSortState).pipe(first()).subscribe(res => sort = res);

        return this._reports.searchReports('prdNameEn', 'ASC', action.query).pipe(
          map((data): any => {
            return ReportsActions.searchSuccess({ reports: data.reports, total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  searchPicked$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.searchPicked),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReportsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReportsSortState).pipe(first()).subscribe(res => sort = res);
        return this._reports.searchPicked('prdNameEn', 'ASC', action.query).pipe(
          map((data): any => {
            return ReportsActions.searchPickedSuccess({ picked: data.picked, picked_total: data.picked_total })
          }),
          catchError(error => {
            return of(ReportsActions.searchPickedFailure({ error }))
          })
        )
      })
    );
  });

  searchProductSerialNumber$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.searchProductSerialNumber),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReportsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReportsSortState).pipe(first()).subscribe(res => sort = res);
        return this._reports.searchProductSerialNumber('prdNameEn', 'ASC', action.query, action.prdID).pipe(
          map((data): any => {
            return ReportsActions.searchProductSerialNumberSuccess({ productSerialNumber: data.serial_numbers.data, product_serialnumber_total: data.total })
          }),
          catchError(error => {
            return of(ReportsActions.searchProductSerialNumberFailure({ error }))
          })
        )
      })
    );
  });

  searchReceived$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.searchReceived),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReportsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReportsSortState).pipe(first()).subscribe(res => sort = res);
        return this._reports.searchReceived('prdNameEn', 'ASC', action.query).pipe(
          map((data): any => {
            return ReportsActions.searchReceivedSuccess({ received: data.received, received_total: data.received_total })
          }),
          catchError(error => {
            return of(ReportsActions.searchReceivedFailure({ error }))
          })
        )
      })
    );
  });

  searchReorder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.searchReorder),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReportsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReportsSortState).pipe(first()).subscribe(res => sort = res);
        return this._reports.searchReorder('prdNameEn', 'ASC', action.query).pipe(
          map((data): any => {
            return ReportsActions.searchReorderSuccess({ reorder: data.reorder, reorder_total: data.reorder_total })
          }),
          catchError(error => {
            return of(ReportsActions.searchReorderFailure({ error }))
          })
        )
      })
    );
  });

  loadReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.loadReport),
      switchMap(action => {
        return this._reports.getReport(action.prdID).pipe(
          map(({ report }): any => {
            return ReportsActions.loadReportSuccess({ report })
          }),
          catchError(error => {
            return of(ReportsActions.loadReportFailure({ error }))
          })
        )
      })
    );
  });

  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.setSort),
      withLatestFrom(this.store$.select(selectCoreState)),
      switchMap(([action, coreState]) => {
        const locID = coreState?.me?.locID;
        return this._reports.getReports(action.sort, action.direction, action.query, null, locID).pipe(
          map((data): any => {
            return ReportsActions.loadReportsSuccess({ reports: data.reports, total: data.total })
          })
        )
      })
    );
  });

  setReorderSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.setReorderSort),
      withLatestFrom(this.store$.select(selectCoreState)),
      switchMap(([action, coreState]) => {
        const locID = coreState?.me?.locID;
        return this._reports.getReportsReorder(action.sort, action.direction, action.query, null, locID).pipe(
          map((data): any => {
            return ReportsActions.loadReorderSuccess({ reorder: data.reorder, reorder_total: data.reorder_total })
          })
        )
      })
    );
  });

  setReceivedSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.setReceivedSort),
      switchMap(action => {
        return this._reports.getReportsReceived(action.sort, action.direction, action.query).pipe(
          map((data): any => {
            return ReportsActions.loadReceivedSuccess({ received: data.received, received_total: data.received_total })
          })
        )
      })
    );
  });

  setPickedSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.setPickedSort),
      switchMap(action => {
        return this._reports.getReportsPicked(action.sort, action.direction, action.query).pipe(
          map((data): any => {
            return ReportsActions.loadPickedSuccess({ picked: data.picked, picked_total: data.picked_total })
          })
        )
      })
    );
  });

  setProductSerialNumberSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.setProductSerialNumberSort),
      switchMap(action => {
        return this._reports.getReportsProductSerialNumber(action.sort, action.direction, action.query,'', action.prdID).pipe(
          map((data): any => {
            return ReportsActions.loadProductSerialNumberSuccess({ productSerialNumber: data.serial_numbers.data, product_serialnumber_total: data.total })
          })
        )
      })
    );
  });
  
  constructor(
    private actions$: Actions, private _reports: ReportsService,
    private store$: Store, private _categories: CategoriesService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
